import { env } from "@/config/env";
import { api, unwrap } from "@/lib/api";
import { useAuthStore } from "@/stores/auth.store";

// ─── Types ────────────────────────────────────────────────────────────────
export type MarketingType = "publication" | "publicite";
export type MarketingMode = "grouped" | "per_product";
export type MarketingStatus =
	| "queued"
	| "generating"
	| "draft"
	| "failed"
	| "published";
export type VisualFormat = "square" | "portrait" | "landscape";

export interface PublicationContent {
	message: string;
	hashtags: string[];
}
export interface PubliciteContent {
	primaryText: string;
	headline: string;
	description: string;
	cta: string;
}
export type MarketingContent = PublicationContent | PubliciteContent;

export interface MarketingVisual {
	url: string;
	mediaId: string;
	format: VisualFormat;
}

export interface MarketingProductSnapshot {
	id: string;
	title: string;
	price: number | null;
	compareAtPrice: number | null;
	currency: string;
	image: string | null;
	url: string | null;
	brand: string | null;
}

export interface MarketingDraft {
	id: string;
	type: MarketingType;
	mode: MarketingMode;
	platforms: string[];
	tone: string | null;
	brief: string | null;
	productIds: string[];
	productSnapshots: MarketingProductSnapshot[];
	content: MarketingContent | null;
	visuals: MarketingVisual[];
	status: MarketingStatus;
	error: string | null;
	createdByLabel: string;
	createdAt: string;
	finishedAt: string | null;
}

export interface MarketingStatusInfo {
	textEnabled: boolean;
	imageEnabled: boolean;
}

export interface GeneratePayload {
	type: MarketingType;
	productIds: string[];
	platforms?: string[];
	tone?: string;
	brief?: string;
}

export interface GenerateResult {
	content: MarketingContent;
	productSnapshots: MarketingProductSnapshot[];
}

export type OnText = (delta: string) => void;

/**
 * Consomme le flux SSE de génération de texte marketing. `onText` reçoit chaque
 * fragment ; la promesse résout avec l'objet final (`event: result`).
 */
async function streamGenerate(
	payload: GeneratePayload,
	onText: OnText,
): Promise<GenerateResult> {
	const token = useAuthStore.getState().accessToken;
	const res = await fetch(`${env.apiUrl}/admin/marketing/generate`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			...(token ? { Authorization: `Bearer ${token}` } : {}),
		},
		body: JSON.stringify(payload),
	});

	if (res.status === 401) {
		useAuthStore.getState().logout();
		throw new Error("Session expirée, reconnecte-toi.");
	}
	if (!res.ok || !res.body) {
		let msg = `Erreur ${res.status}`;
		try {
			const j = (await res.json()) as { message?: string | string[] };
			msg = Array.isArray(j.message) ? j.message.join(", ") : (j.message ?? msg);
		} catch {
			/* corps non JSON */
		}
		throw new Error(msg);
	}

	const reader = res.body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let result: GenerateResult | null = null;
	let errorMsg: string | null = null;

	const handleBlock = (block: string): void => {
		let eventName = "message";
		const dataLines: string[] = [];
		for (const line of block.split("\n")) {
			if (line.startsWith("event:")) eventName = line.slice(6).trim();
			else if (line.startsWith("data:"))
				dataLines.push(line.slice(5).replace(/^ /, ""));
		}
		if (!dataLines.length) return;
		const data = dataLines.join("\n");
		if (eventName === "delta") {
			try {
				onText((JSON.parse(data) as { text?: string }).text ?? "");
			} catch {
				/* ignore */
			}
		} else if (eventName === "result") {
			try {
				result = JSON.parse(data) as GenerateResult;
			} catch {
				/* ignore */
			}
		} else if (eventName === "error") {
			try {
				errorMsg =
					(JSON.parse(data) as { message?: string }).message ?? "Erreur IA.";
			} catch {
				errorMsg = "Erreur IA.";
			}
		}
	};

	for (;;) {
		const { done, value } = await reader.read();
		if (done) break;
		buffer += decoder.decode(value, { stream: true });
		let idx: number;
		while ((idx = buffer.indexOf("\n\n")) !== -1) {
			handleBlock(buffer.slice(0, idx));
			buffer = buffer.slice(idx + 2);
		}
	}
	if (buffer.trim()) handleBlock(buffer);

	if (errorMsg) throw new Error(errorMsg);
	if (result === null) throw new Error("L'agent IA n'a pas renvoyé de résultat.");
	return result;
}

export const marketingApi = {
	status: () =>
		unwrap<MarketingStatusInfo>(api.get("/admin/marketing/status")),
	generateStream: streamGenerate,
	saveDraft: (dto: {
		type: MarketingType;
		productIds: string[];
		platforms?: string[];
		tone?: string;
		brief?: string;
		content: MarketingContent;
	}) => unwrap<MarketingDraft>(api.post("/admin/marketing/drafts", dto)),
	batch: (dto: GeneratePayload) =>
		unwrap<{ created: MarketingDraft[]; skipped: number }>(
			api.post("/admin/marketing/drafts/batch", dto),
		),
	list: (params?: { type?: MarketingType; status?: MarketingStatus; page?: number; pageSize?: number }) =>
		unwrap<{
			data: MarketingDraft[];
			total: number;
			page: number;
			pageSize: number;
		}>(api.get("/admin/marketing/drafts", { params })),
	get: (id: string) =>
		unwrap<MarketingDraft>(api.get(`/admin/marketing/drafts/${id}`)),
	updateContent: (id: string, content: MarketingContent) =>
		unwrap<MarketingDraft>(
			api.patch(`/admin/marketing/drafts/${id}`, { content }),
		),
	remove: (id: string) => api.delete(`/admin/marketing/drafts/${id}`),
	retry: (id: string) =>
		unwrap<MarketingDraft>(api.post(`/admin/marketing/drafts/${id}/retry`)),
	generateImage: (dto: {
		productId: string;
		draftId?: string;
		format?: VisualFormat;
		tone?: string;
		brief?: string;
		cta?: string;
	}) =>
		unwrap<{ ok: boolean; visual?: MarketingVisual; error?: string }>(
			api.post("/admin/marketing/generate-image", dto),
		),
};
